import { datadogRum } from '@datadog/browser-rum';
import { getDataDogRumInitOptions } from './config.datadog';
import type { GetSessionId } from './beforeSendRum.datadog';
import type {
  CustomDatadogContext,
  CustomDatadogContextKeys,
  DataDogUser,
} from './context.datadog';

export type GetLocale = () => string | undefined;

/**
 * Initializes DataDog RUM (Real User Monitoring) with default configurations.
 *
 * @param _datadogRum [DO NOT USE] - Internal parameter for Dependency Injection.
 * @returns {void}
 */
export const initDataDogRum = (
  getSessionId: GetSessionId,
  getLocale: GetLocale,
  /* istanbul ignore next */
  _datadogRum: typeof datadogRum = datadogRum,
): void => {
  _datadogRum.init(getDataDogRumInitOptions(getSessionId));

  const locale = getLocale();
  if (locale !== undefined) {
    _datadogRum.setGlobalContextProperty('locale', locale);
  }
  _datadogRum.startSessionReplayRecording();
};

/**
 * Sets a global context property for DataDog RUM.
 *
 * @template Value - The type of the value being set.
 * @param key - The key for the global context property.
 * @param value - The value associated with the key.
 * @param _datadogRum [DO NOT USE] - Internal parameter for Dependency Injection.
 * @returns {void}
 */
export const setRumGlobalContextProp = <Value>(
  key: CustomDatadogContextKeys,
  value: Value,
  /* istanbul ignore next */
  _datadogRum: typeof datadogRum = datadogRum,
): void => {
  _datadogRum.setGlobalContextProperty(key, value);
};

/**
 * Removes a global context property from DataDog RUM.
 *
 * @param key - The key of the global context property to be removed.
 * @param _datadogRum [DO NOT USE] - Internal parameter for Dependency Injection.
 * @returns {void}
 */
export const removeRumGlobalContextProp = (
  key: CustomDatadogContextKeys,
  /* istanbul ignore next */
  _datadogRum: typeof datadogRum = datadogRum,
): void => {
  _datadogRum.removeGlobalContextProperty(key);
};

/**
 * Sets multiple global context properties for DataDog RUM.
 *
 * @param properties - Key-value pairs to be set in the global context.
 * @param _datadogRum [DO NOT USE] - Internal parameter for Dependency Injection.
 * @returns {void}
 */
export const setRumGlobalContextProps = (
  properties: CustomDatadogContext,
  /* istanbul ignore next */
  _datadogRum: typeof datadogRum = datadogRum,
): void => {
  for (const [key, value] of Object.entries(properties)) {
    _datadogRum.setGlobalContextProperty(key, value);
  }
};

/**
 * Removes all global context properties present in the given context object.
 * Symmetric counterpart to `setRumGlobalContextProps` — pass the same object
 * to clean up exactly the keys that were previously set.
 *
 * @param context - The context object whose keys should be removed.
 * @param _datadogRum [DO NOT USE] - Internal parameter for Dependency Injection.
 * @returns {void}
 */
export const removeRumGlobalContextProps = (
  context: CustomDatadogContext,
  /* istanbul ignore next */
  _datadogRum: typeof datadogRum = datadogRum,
): void => {
  for (const key of Object.keys(context)) {
    _datadogRum.removeGlobalContextProperty(key);
  }
};

/**
 * Sets user information for DataDog RUM.
 *
 * @param user - The user data to be set.
 * @param _datadogRum [DO NOT USE] - Internal parameter for Dependency Injection.
 * @returns {void}
 */
export const setRumUser = (
  user: DataDogUser,
  /* istanbul ignore next */
  _datadogRum: typeof datadogRum = datadogRum,
): void => {
  _datadogRum.setUser(user);
};
